// Library methods

// Utils
import { POST } from '../../../utils/fetchWrappers';

// API URLs
import {
    API_GET_ALL_USERS,
    API_CREATE_USER,
    API_GET_ALL_ROLES,
    API_ENTITY_LIST,
    API_UPDATE_USER,
    API_CREATE_ADMIN_USER,
    API_GET_ALL_USER_MAKERS,
    API_UPDATE_ADMIN_USER,
} from '../../../utils/API/apiUrls';

// API Status Codes
import { apiStatusCodes } from '../../../utils/API/apiStatusCodes';

// Redux Actions
import * as userManagementActions from './userManagement.actions';

// Constants
import {
    FETCH_USER_REQUEST,
    CREATE_USER_REQUEST,
    GET_USER_ROLES_REQUEST,
    FETCH_ENTITY_LIST_REQUEST,
    UPDATE_USER_REQUEST,
    CREATE_ADMIN_USER_REQUEST,
    UPDATE_ADMIN_USER_REQUEST,
    FETCH_USER_MAKER_REQUEST,
    FETCH_SYSTEM_USERS_REQUEST,
} from './userManagement.constants';
import moment from 'moment';
import { takeLatest, put, call } from 'redux-saga/effects';

const getRequestData = payload => {
    const request = {
        page: payload.pageIndex || 1,
        resultPerPage: payload.pageSize || 10,
        userFilter: {},
    };
    if (payload.createdOn) {
        request.userFilter.createdStartDate = moment(
            payload.createdOn.startDate
        ).startOf('day');
        request.userFilter.createdEndDate = moment(
            payload.createdOn.endDate
        ).endOf('day');
    }
    if (payload.role) {
        request.userFilter.roleName = payload.role;
    }
    if (payload.createdBy) {
        request.userFilter.userCreatedByName = payload.createdBy;
    }
    if (payload.userLevel) {
        request.userFilter.userEntityType = payload.userLevel;
    }
    if (payload.status) {
        request.userFilter.userStatus = payload.status;
    }
    return request;
};

export function* createUserDetails(actions) {
    try {
        const { payload } = actions;
        const request = { ...payload };
        const { data } = yield call(POST, API_CREATE_USER, request);
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(
                userManagementActions.createUserSuccess({
                    isLoading: false,
                    data,
                    message: '',
                })
            );
        } else if (data && data.errorMessage) {
            yield put(
                userManagementActions.createUserError({
                    isLoading: false,
                    message: data.errorMessage,
                })
            );
        }
    } catch ({ response }) {
        const { data, status } = response;

        if (status !== 401) {
            yield put(
                userManagementActions.createUserError({
                    isLoading: false,
                    message: data.errorMessage,
                })
            );
        }
    }
}

export function* fetchUserDetails(actions) {
    yield put(userManagementActions.showLoader());
    try {
        const { payload } = actions;
        const request = getRequestData(payload);
        const { data } = yield call(POST, API_GET_ALL_USERS, request);
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(userManagementActions.fetchUserSuccess(data));
        } else if (data.statusCode === apiStatusCodes.NORECORD) {
            yield put(
                userManagementActions.fetchUserError({
                    error: {},
                })
            );
        } else if (data && data.errorMessage) {
            yield put(
                userManagementActions.fetchUserError({
                    error: data,
                })
            );
        }
    } catch ({ response }) {
        const { data } = response;
        yield put(userManagementActions.fetchUserError({ error: data }));
    }
    yield put(userManagementActions.hideLoader());
}

export function* fetchSystemUsersDetails(actions) {
    try {
        const { payload } = actions;
        const { data } = yield call(POST, API_GET_ALL_USERS, payload);
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(userManagementActions.fetchSystemUsersSuccess(data));
        } else if (data.statusCode === apiStatusCodes.NORECORD) {
            yield put(
                userManagementActions.fetchSystemUsersError({
                    error: {},
                })
            );
        } else if (data && data.errorMessage) {
            yield put(
                userManagementActions.fetchSystemUsersRequest({
                    error: data,
                })
            );
        }
    } catch ({ response }) {
        const { data } = response;
        yield put(userManagementActions.fetchUserError({ error: data }));
    }
}

export function* getAllRolesDetails() {
    try {
        const request = {
            page: 1,
            resultPerPage: 100,
        };
        const { data } = yield call(POST, API_GET_ALL_ROLES, request);
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(userManagementActions.getAllRolesSuccess(data));
        } else if (data && data.errorMessage) {
            yield put(
                userManagementActions.getAllRolesError({
                    error: data.errorMessage,
                })
            );
        }
    } catch ({ response }) {
        const { data } = response;
        const errorMessage = data && data.errorMessage ? data.errorMessage : '';
        yield put(
            userManagementActions.getAllRolesError({ error: errorMessage })
        );
    }
}

export function* fetchUserMakerDetails(actions) {
    yield put(userManagementActions.showLoader());
    try {
        const { payload } = actions;
        const { data } = yield call(POST, API_GET_ALL_USER_MAKERS, payload);
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(userManagementActions.getUserMakersDetailsSuccess(data));
        } else if (data.statusCode === apiStatusCodes.NORECORD) {
            yield put(
                userManagementActions.getUserMakersDetailsError({
                    error: {},
                })
            );
        } else if (data && data.errorMessage) {
            yield put(
                userManagementActions.getUserMakersDetailsError({
                    error: data.errorMessage,
                })
            );
        }
    } catch ({ response }) {
        const { data } = response;
        const errorMessage = data && data.errorMessage ? data.errorMessage : '';
        yield put(
            userManagementActions.getUserMakersDetailsError({
                error: errorMessage,
            })
        );
    }
    yield put(userManagementActions.hideLoader());
}

export function* getEntityDetails(actions) {
    try {
        const { payload } = actions;
        const { data } = yield call(POST, API_ENTITY_LIST, payload);
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(userManagementActions.fetchEntitySuccess(data));
        } else if (data && data.errorMessage) {
            yield put(
                userManagementActions.fetchEntityError({
                    error: data.errorMessage,
                })
            );
        }
    } catch ({ response }) {
        const { data } = response;
        const errorMessage = data && data.errorMessage ? data.errorMessage : '';
        yield put(
            userManagementActions.fetchEntityError({ error: errorMessage })
        );
    }
}

const getUserDataRequest = payload => {
    const updateEnumList = [
        'NAME_UPDATE',
        'ENTITY_UPDATE',
        'ROLE_UPDATE',
        'USER_STATUS_UPDATE',
    ];
    const request = {
        comment: payload.remarks || '',
        entityId: payload.entityId || '',
        entityName: payload.entityName || '',
        entityType: payload.userLevel || '',
        name: payload.name || '',
        roleId: payload.userRole,
        updateEnumList:
            payload.userLevel === 'RM'
                ? updateEnumList.filter(item => item !== 'ENTITY_UPDATE')
                : updateEnumList,
        userId: payload.userId,
        userStatus: payload.userStatus || '',
        level: payload.uLevel || null,
        parentUserId: payload.supervisor || '',
    };
    return request;
};

const getAdminUserDataRequest = payload => {
    const updateEnumList = ['NAME_UPDATE', 'ROLE_UPDATE', 'USER_STATUS_UPDATE'];
    const request = {
        comment: payload.remarks || '',
        name: payload.name || '',
        roleId: payload.userRole,
        updateEnumList,
        userId: payload.userId,
        userStatus: payload.userStatus || '',
        level: payload.uLevel || null,
        parentUserId: payload.supervisor || '',
    };
    return request;
};

export function* updateUserDetails(actions) {
    try {
        const { payload } = actions;
        const request = getUserDataRequest(payload);
        const { data } = yield call(POST, API_UPDATE_USER, request);
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(
                userManagementActions.updateUserSuccess({
                    isLoading: false,
                    data,
                    message: '',
                })
            );
        } else if (data && data.errorMessage) {
            yield put(
                userManagementActions.updateUserError({
                    isLoading: false,
                    message: data.errorMessage,
                })
            );
        }
    } catch ({ response }) {
        const { data, status } = response;

        if (status !== 401) {
            yield put(
                userManagementActions.updateUserError({
                    isLoading: false,
                    message: data.errorMessage,
                })
            );
        }
    }
}

export function* createAdminUserDetails(actions) {
    try {
        const { payload } = actions;
        const request = { ...payload };
        const { data } = yield call(POST, API_CREATE_ADMIN_USER, request);
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(
                userManagementActions.createAdminUserSuccess({
                    isLoading: false,
                    data,
                    message: '',
                })
            );
        } else if (data && data.errorMessage) {
            yield put(
                userManagementActions.createAdminUserError({
                    isLoading: false,
                    message: data.errorMessage,
                })
            );
        }
    } catch ({ response }) {
        const { data, status } = response;

        if (status !== 401) {
            yield put(
                userManagementActions.createAdminUserError({
                    isLoading: false,
                    message: data.errorMessage,
                })
            );
        }
    }
}

export function* updateAdminUserDetails(actions) {
    try {
        const { payload } = actions;
        const request = getAdminUserDataRequest(payload);
        const { data } = yield call(POST, API_UPDATE_ADMIN_USER, request);
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(
                userManagementActions.updateAdminUserSuccess({
                    isLoading: false,
                    data,
                    message: '',
                })
            );
        } else if (data && data.errorMessage) {
            yield put(
                userManagementActions.updateAdminUserError({
                    isLoading: false,
                    message: data.errorMessage,
                })
            );
        }
    } catch ({ response }) {
        const { data, status } = response;

        if (status !== 401) {
            yield put(
                userManagementActions.updateAdminUserError({
                    isLoading: false,
                    message: data.errorMessage,
                })
            );
        }
    }
}

/**
 * WATCHER SAGA
 */
export function* userManagementSaga() {
    yield takeLatest(FETCH_USER_REQUEST, fetchUserDetails);
    yield takeLatest(CREATE_USER_REQUEST, createUserDetails);
    yield takeLatest(GET_USER_ROLES_REQUEST, getAllRolesDetails);
    yield takeLatest(FETCH_ENTITY_LIST_REQUEST, getEntityDetails);
    yield takeLatest(UPDATE_USER_REQUEST, updateUserDetails);
    yield takeLatest(CREATE_ADMIN_USER_REQUEST, createAdminUserDetails);
    yield takeLatest(UPDATE_ADMIN_USER_REQUEST, updateAdminUserDetails);
    yield takeLatest(FETCH_USER_MAKER_REQUEST, fetchUserMakerDetails);
    yield takeLatest(FETCH_SYSTEM_USERS_REQUEST, fetchSystemUsersDetails);
}
